/**
 * A unit of the company of agents — Spec 035 v0.4. Its place in the tree, its head (people talk
 * to the heads of top units only), its staff and nested units, its visiting card. People see all
 * of it and change it here; archiving and restoring are theirs only, in the app. Step 2: the board
 * of orders, and for a top unit the people's orders and conversation with its head. Step 3: how
 * its head is launched, and «Запустить подразделение». Step 4: its events and subscriptions.
 * Step 5: a top unit's decisions for people. Step 6: its month's token limit and spending.
 * Spec 039 step 3: its figures on top, the decisions next, and its staff as the company view
 * sees them — where each works, since when it is idle, how long it worked, what it spent.
 * Spec 055 step 3: the tab «Набор» — the unit's kit, kept in the address (?tab=kit).
 */

import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";

import { ApiError } from "@/api/client";
import { Departments, type DepartmentOut, type UnitMemberOut } from "@/api/units";
import { HelperRows, StaffTable, UnitSummary, useCompanyView } from "@/components/CompanyStaff";
import { ServicesSheet, UnitSheet } from "@/components/UnitForm";
import { BudgetBlock } from "@/components/UnitBudget";
import { UnitDecisionsBlock } from "@/components/UnitDecisions";
import { UnitEventsBlock } from "@/components/UnitEvents";
import { UnitKitTab } from "@/components/UnitKitTab";
import { LaunchBlock } from "@/components/UnitLaunch";
import { GiveOrderSheet, OrderDetailSheet, OrdersBoard, UnitConversation } from "@/components/UnitWork";
import { Button } from "@/components/ui/Button";
import { Segmented } from "@/components/ui/Segmented";
import { SkeletonList } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { AppBar, Caption, EmptyNote, Pill } from "@/kit";
import { staffOfUnit } from "@/lib/company";
import { memberMarks } from "@/lib/lessons";
import { KIND_LABEL, pathLabel, RUN_LABEL, runTone, staffOf } from "@/lib/units";
import { useBackButton } from "@/lib/telegram-ui";

const TABS: { value: "unit" | "kit"; label: string }[] = [
  { value: "unit", label: "Отдел" },
  { value: "kit", label: "Набор" },
];

export function DepartmentScreenRoute() {
  useBackButton("/departments");
  const navigate = useNavigate();
  const { department_id } = useParams<{ department_id: string }>();
  return department_id ? (
    <DepartmentScreen departmentId={department_id} onOpen={(id) => navigate(`/departments/${id}`)} />
  ) : null;
}

/** Until the company view comes: the staff from the unit itself. */
function MemberRow({ member, head = false }: { member: UnitMemberOut; head?: boolean }) {
  const navigate = useNavigate();
  return (
    <li className="border-b border-p-border last:border-0">
      <button type="button" onClick={() => navigate(`/agents/${member.agent_id}`)}
        className="flex w-full items-start gap-3 px-4 py-3 text-left press-surface">
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="text-[15px] font-semibold leading-5 text-p-text">
            {member.name}
            {head && <span className="ml-2 text-meta font-normal text-p-text-muted">руководитель</span>}
          </span>
          {member.purpose_md && <span className="line-clamp-2 text-meta text-p-text-muted">{member.purpose_md}</span>}
          <span className="flex flex-wrap gap-1">
            {member.waits_launch ? (
              <Pill tone="needs">ждёт запуска</Pill>
            ) : (
              <Pill tone={runTone(member.run_state) === "done" ? "done" : runTone(member.run_state) === "review" ? "review" : "waiting"}>
                {RUN_LABEL[member.run_state]}
              </Pill>
            )}
            {/* Spec 037: an incomplete instruction, and the lessons out of 20. */}
            {memberMarks(member).map((m) => <Pill key={m.label} tone={m.tone}>{m.label}</Pill>)}
          </span>
          {member.waits_launch && (
            <span className="text-meta text-p-text-muted">Для него есть работа: открой и нажми «Запустить».</span>
          )}
        </span>
        <ChevronRight className="mt-0.5 h-5 w-5 shrink-0 text-p-text-muted" aria-hidden="true" />
      </button>
      {/* Spec 042: its helpers sit under it, in the unit with it. */}
      {(member.helpers ?? []).length > 0 && <HelperRows helpers={member.helpers!} />}
    </li>
  );
}

/** `embedded`: in the right pane of «Отделы» on a computer — no way back of its own. */
export function DepartmentScreen({ departmentId, embedded = false, onOpen }: {
  departmentId: string;
  embedded?: boolean;
  onOpen: (id: string) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const one = useQuery({ queryKey: ["departments", "one", departmentId], queryFn: () => Departments.get(departmentId) });
  const all = useQuery({ queryKey: ["departments", "list", true], queryFn: () => Departments.list(true) });
  const company = useCompanyView();
  const [sheet, setSheet] = useState<"edit" | "nested" | "services" | "order" | null>(null);
  const [orderId, setOrderId] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const tab: "unit" | "kit" = params.get("tab") === "kit" ? "kit" : "unit";

  const archive = useMutation({
    mutationFn: (unit: DepartmentOut) =>
      unit.archived_at ? Departments.restore(unit.id, unit.version) : Departments.archive(unit.id, unit.version),
    onSuccess: (saved) => {
      void qc.invalidateQueries({ queryKey: ["departments"] });
      void qc.invalidateQueries({ queryKey: ["company"] });
      toast.success(saved.archived_at ? "Подразделение в архиве" : "Подразделение вернулось из архива");
    },
    onError: (e: Error) => {
      toast.error(e.message);
      if (e instanceof ApiError && e.status === 409) void qc.invalidateQueries({ queryKey: ["departments"] });
    },
  });

  const bar = <AppBar fallback="/departments" context="Отделы" back={!embedded} />;
  if (one.isLoading) return <div className="flex flex-col gap-3">{bar}<SkeletonList count={3} /></div>;
  if (one.isError || !one.data) {
    return (
      <div className="flex flex-col gap-3">
        {bar}
        <EmptyNote action={<Button size="compact" variant="secondary" onClick={() => void one.refetch()}>Повторить</Button>}>
          Не удалось открыть подразделение.
        </EmptyNote>
      </div>
    );
  }
  const unit = one.data;
  const units = all.data ?? [];
  const nested = units.filter((u) => u.parent_id === unit.id && !u.archived_at);
  const staff = staffOf(unit);
  // Spec 042: the helpers by their staff member, for the company view's rows.
  const helpersOf = Object.fromEntries(unit.members.map((m) => [m.agent_id, m.helpers ?? []]));
  const parent = unit.parent_id ? units.find((u) => u.id === unit.parent_id) : undefined;
  const live = !unit.archived_at;
  // The view of the whole company: this unit in it, and the moment it was taken.
  const view = company.data;
  const viewUnit = view?.units.find((u) => u.id === unit.id);
  const viewStaff = view && viewUnit ? staffOfUnit(view, unit.id) : null;
  const now = company.dataUpdatedAt;

  return (
    <div className="flex flex-col gap-4">
      {bar}
      <header className="flex flex-col gap-1">
        <span className="text-meta text-p-text-muted">
          {KIND_LABEL[unit.kind]}
          {unit.depth > 1 && " · "}
          {unit.depth > 1 && parent && (
            <button type="button" className="font-semibold text-p-link" onClick={() => onOpen(parent.id)}>
              {pathLabel(parent)}
            </button>
          )}
        </span>
        <h1 className="text-[26px] font-bold leading-8 text-p-text">{unit.name}</h1>
        {unit.purpose_md && <p className="text-body text-p-text-muted">{unit.purpose_md}</p>}
        {unit.archived_at && <p className="text-meta text-p-needs">В архиве: не меняется и не берёт работу.</p>}
      </header>

      <Segmented options={TABS} value={tab} label="Раздел отдела"
        onChange={(v) => setParams((prev) => {
          // Other parameters of the address stay: the unit may sit in the pane of «Отделы».
          const next = new URLSearchParams(prev);
          if (v === "kit") next.set("tab", "kit");
          else next.delete("tab");
          return next;
        }, { replace: true })} />

      {tab === "kit" && <UnitKitTab unitId={unit.id} live={live} />}

      {tab === "unit" && (
        <>

      {live && (
        <div className="flex flex-wrap gap-2">
          <Button size="compact" variant="secondary" onClick={() => setSheet("edit")}>Изменить</Button>
          <Button size="compact" variant="secondary" onClick={() => setSheet("nested")}>Вложенное подразделение</Button>
          <Button size="compact" variant="secondary" onClick={() => setSheet("services")}>Визитка</Button>
        </div>
      )}

      {live && viewUnit && viewStaff && <UnitSummary unit={viewUnit} staff={viewStaff} />}

      {unit.depth === 1 && (
        <section className="flex flex-col gap-2" aria-label="Решения для людей">
          <Caption>Решения для людей</Caption>
          <UnitDecisionsBlock unitId={unit.id} />
        </section>
      )}

      <section className="flex flex-col gap-2" aria-label="Сотрудники">
        <Caption>Сотрудники</Caption>
        {!unit.head && (
          <p className="text-meta text-p-needs">Руководителя нет — подразделение не принимает поручений. Назначь его в «Изменить».</p>
        )}
        {viewStaff ? (
          <StaffTable staff={viewStaff} now={now} helpers={helpersOf} />
        ) : unit.head || staff.length ? (
          <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
            {unit.head && <MemberRow member={unit.head} head />}
            {staff.map((m) => <MemberRow key={m.agent_id} member={m} />)}
          </ul>
        ) : (
          <p className="text-meta text-p-text-muted">Сотрудников нет.</p>
        )}
        {live && unit.head && <LaunchBlock unit={unit} />}
      </section>

      <section className="flex flex-col gap-2" aria-label="Поручения">
        <div className="flex items-center justify-between gap-2">
          <Caption>Поручения</Caption>
          {live && unit.depth === 1 && unit.head && (
            <Button size="compact" variant="secondary" onClick={() => setSheet("order")}>Дать поручение</Button>
          )}
        </div>
        <OrdersBoard unitId={unit.id} onOpen={setOrderId} />
      </section>

      {unit.depth === 1 && (
        <section className="flex flex-col gap-2" aria-label="Разговор с руководителем">
          <Caption>Разговор с руководителем</Caption>
          <UnitConversation unit={unit} />
        </section>
      )}

      <section className="flex flex-col gap-2" aria-label="Бюджет">
        <Caption>Бюджет месяца</Caption>
        <BudgetBlock unitId={unit.id} live={live} />
      </section>

      <section className="flex flex-col gap-2" aria-label="События">
        <Caption>События</Caption>
        <UnitEventsBlock unitId={unit.id} />
      </section>

      {nested.length > 0 && (
        <section className="flex flex-col gap-2" aria-label="Вложенные подразделения">
          <Caption>Внутри</Caption>
          <ul className="overflow-hidden rounded-card border border-p-border bg-p-surface">
            {nested.map((u) => (
              <li key={u.id} className="border-b border-p-border last:border-0">
                <button type="button" onClick={() => onOpen(u.id)} className="flex w-full items-start gap-3 px-4 py-3 text-left press-surface">
                  <span className="flex min-w-0 flex-1 flex-col gap-1">
                    <span className="text-[15px] font-semibold leading-5 text-p-text">{u.name}</span>
                    <span className="text-meta text-p-text-muted">
                      {KIND_LABEL[u.kind]} · {u.head ? `руководитель ${u.head.name}` : "нет руководителя"}
                    </span>
                  </span>
                  <ChevronRight className="mt-0.5 h-5 w-5 shrink-0 text-p-text-muted" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="flex flex-col gap-2" aria-label="Визитка">
        <Caption>Визитка</Caption>
        {unit.services.length ? (
          <ul className="flex flex-col gap-2">
            {unit.services.map((s) => (
              <li key={s.key} className="rounded-card border border-p-border bg-p-surface px-4 py-3">
                <p className="text-[15px] font-semibold text-p-text">{s.title}</p>
                {s.input_md && <p className="text-meta text-p-text-muted">На входе: {s.input_md}</p>}
                {s.output_md && <p className="text-meta text-p-text-muted">На выходе: {s.output_md}</p>}
                <p className="text-meta text-p-text">Делает: {s.agent_name ?? "руководитель решает сам"}</p>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-meta text-p-text-muted">Услуг пока нет: другие подразделения заказывают работу у руководителя.</p>
        )}
      </section>

      <section className="flex flex-col gap-2" aria-label="Люди">
        <Caption>Люди</Caption>
        <p className="text-meta text-p-text-muted">
          {unit.people.map((p) => p.name).join(", ") || "никого"}
          {unit.people_inherited ? " — от верхнего подразделения" : " — решают за него и получают сводку"}
        </p>
      </section>

      <button type="button" disabled={archive.isPending}
        className="min-h-11 self-start text-meta font-semibold text-p-link disabled:opacity-55"
        onClick={() => archive.mutate(unit)}>
        {unit.archived_at ? "Вернуть из архива" : "Убрать в архив"}
      </button>
        </>
      )}

      {sheet === "edit" && <UnitSheet open unit={unit} onClose={() => setSheet(null)} onSaved={() => setSheet(null)} />}
      {sheet === "nested" && (
        <UnitSheet open parentId={unit.id} onClose={() => setSheet(null)}
          onSaved={(saved) => { setSheet(null); onOpen(saved.id); }} />
      )}
      {sheet === "services" && <ServicesSheet unit={unit} onClose={() => setSheet(null)} />}
      {sheet === "order" && <GiveOrderSheet unit={unit} onClose={() => setSheet(null)} />}
      {orderId && (
        <OrderDetailSheet key={orderId} orderId={orderId} canCancel onClose={() => setOrderId(null)} onOpen={setOrderId} />
      )}
    </div>
  );
}
